import { useEffect, useRef, useState } from 'react'
import { Navigate, useLocation, useParams } from 'react-router-dom'

import { useQuery } from '@apollo/client/react'

import {
  EmptyState,
  Link2OffIcon,
  Loader,
  WarningIcon,
} from '@buffer-mono/popcorn'

import { AccessDenied } from '~publish/components/AccessDenied'
import { useOrganizationSwitcher } from '~publish/components/MainSidebar/components/OrganizationSwitcher/useOrganizationSwitcher'
import { useAccount } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import { classifyPostFetchError } from '~publish/helpers/posts/classifyPostFetchError'
import type { PostFetchFailure } from '~publish/helpers/posts/classifyPostFetchError'
import { isOfType } from '~publish/helpers/typeGuards'
import { useContentItemNotesSplits } from '~publish/hooks/notes/useContentItemNotesEnabled'

import { buildDraftPermalinkTarget } from './buildDraftPermalinkTarget'
import { ContentItemNotesArchive } from './ContentItemNotesArchive'

import styles from '~publish/styles/centered-loader.module.css'

import type { JSX } from 'react'

export const GetContentItemPermalink = graphql(`
  query GetContentItemPermalink($id: ContentItemId!) {
    contentItem(input: { id: $id }) {
      id
      organizationId
      body {
        __typename
        ... on PostContent {
          posts {
            id
            status
            channelId
            channel {
              id
              name
              displayName
              service
            }
          }
        }
      }
    }
  }
`)

/**
 * The shareable form of a channel-less draft. Drafts are only ever rendered
 * inside a list view, so a pasted link resolves into one at open time.
 *
 * `notes` is the form a Note mention links to. A draft opens in its details
 * dialog, whose Notes panel is open by default, as a Post link does. A draft
 * that was scheduled opens its closed thread here, with links to the Posts
 * the reader can see, and still opens when they can see none. The `noteId`
 * the link carries is not used: like a Post link, it opens the thread. With
 * ContentItem Notes off, it behaves as the plain link.
 */
export function ContentItemPermalink({
  notes = false,
}: {
  notes?: boolean
}): JSX.Element {
  const { contentItemId } = useParams<{ contentItemId: string }>()
  const location = useLocation()
  const notesSplits = useContentItemNotesSplits()
  const { account, loading: accountLoading } = useAccount()
  const currentOrganizationId = account?.currentOrganization?.id ?? null
  const { switchOrganization } = useOrganizationSwitcher()
  // Re-renders before the switch navigates away, so latch it.
  const hasStartedSwitchRef = useRef(false)
  const [switchFailed, setSwitchFailed] = useState(false)

  const { data, loading, error } = useQuery(GetContentItemPermalink, {
    variables: { id: contentItemId ?? '' },
    skip: !contentItemId,
  })

  const contentItem = data?.contentItem
  const scheduledBody =
    notes &&
    notesSplits.isEnabled &&
    contentItem &&
    isOfType(contentItem.body, 'PostContent')
      ? contentItem.body
      : null
  // The archive shows here, so an organization switch comes back to this link.
  const target = scheduledBody
    ? `${location.pathname}${location.search}`
    : contentItem
      ? buildDraftPermalinkTarget(contentItem)
      : null

  // `getContentItem` authorizes against the draft's organization, so a cross-org
  // link resolves while the shell stays on the wrong org. Switching writes that
  // organization server-side, and effects run after whichever branch below
  // returned, so the `target` guard keeps a denied reader from being moved.
  const organizationToSwitchTo =
    contentItem &&
    target &&
    currentOrganizationId &&
    contentItem.organizationId !== currentOrganizationId
      ? contentItem.organizationId
      : null

  useEffect(() => {
    if (!organizationToSwitchTo || !target || hasStartedSwitchRef.current) {
      return
    }
    hasStartedSwitchRef.current = true
    switchOrganization(organizationToSwitchTo, { redirectTo: target }).catch(
      // `switchOrganization` has toasted already; this only ends the spinner.
      () => setSwitchFailed(true),
    )
  }, [organizationToSwitchTo, target, switchOrganization])

  if (loading || accountLoading || (notes && !notesSplits.isReady)) {
    return <CenteredLoader />
  }

  if (error || !contentItem) {
    return (
      <DraftPermalinkFailureState failure={classifyPostFetchError(error)} />
    )
  }

  if (!target) {
    return <DraftPermalinkFailureState failure="accessDenied" />
  }

  if (switchFailed) {
    return <DraftPermalinkFailureState failure="unexpected" />
  }

  // The effect navigates once the switch completes.
  if (organizationToSwitchTo) {
    return <CenteredLoader />
  }

  if (contentItem && scheduledBody) {
    return (
      <ContentItemNotesArchive
        contentItemId={contentItem.id}
        posts={scheduledBody.posts}
      />
    )
  }

  return <Navigate to={target} replace />
}

const CenteredLoader = (): JSX.Element => (
  <div className={styles.centeredLoader}>
    <Loader />
  </div>
)

const DraftPermalinkFailureState = ({
  failure,
}: {
  failure: PostFetchFailure
}): JSX.Element => {
  if (failure === 'accessDenied') {
    return (
      <AccessDenied description="Ask an owner or admin for access, then open the link again." />
    )
  }

  if (failure === 'unavailable') {
    return (
      <EmptyState size="large" variant="neutral">
        <EmptyState.Icon>
          <Link2OffIcon />
        </EmptyState.Icon>
        <EmptyState.Heading>
          This draft isn&apos;t available anymore
        </EmptyState.Heading>
        <EmptyState.Description>
          It looks like this draft was deleted. Check with whoever shared the
          link if you still need it.
        </EmptyState.Description>
      </EmptyState>
    )
  }

  return (
    <EmptyState size="large" variant="critical">
      <EmptyState.Icon>
        <WarningIcon />
      </EmptyState.Icon>
      <EmptyState.Heading>Something went wrong</EmptyState.Heading>
      <EmptyState.Description>
        We couldn&apos;t open this draft. Try the link again, or check with
        whoever shared it.
      </EmptyState.Description>
    </EmptyState>
  )
}
